/**
 * Plain-language explanations for the special gamemodes, read from the same
 * constants the simulation runs on so the copy cannot drift from what
 * actually happens.
 *
 * They are for someone who has never played. Every number quoted below comes
 * from `@interlock/protocol`'s `limits.ts` or from `@interlock/sim` — this
 * module never invents one — so a future change to a curse's duration or the
 * rope's scale updates this copy automatically rather than leaving a stale
 * sentence somewhere in the UI.
 */
import {
  AI_LEVELS,
  CURSE_COOLDOWN_MS,
  CURSE_EFFECT_DURATION_MS,
  CURSE_TRIGGER_MIN_ATTACK,
  HANDICAP_GUEST_ATTACK_EASE,
  HANDICAP_GUEST_GRAVITY_EASE_PERMILLE,
  HANDICAP_MAX_ATTACK_EASE,
  HANDICAP_MAX_GRAVITY_EASE_PERMILLE,
  HANDICAP_MIN_RECORDED_MATCHES,
  ROUNDS_TO_WIN,
  TUG_ROPE_SCALE,
} from '@interlock/protocol';
import { CURSE_RUSH_GRAVITY_FACTOR } from '@interlock/sim';
import type { AiLevel, RoomVariant } from '@interlock/protocol';
import type { CurseEffect } from '@interlock/sim';

const seconds = (ms: number): string => `${String(ms / 1000)}s`;

/**
 * One line per curse, with the real duration folded in — "FOG — clouds your
 * stack for 8s" rather than the shorter, number-free copy
 * `describeCurseEffectDetail` (`packages/protocol`) already carries for a
 * tooltip's worth of space. `CURSE_TRIGGER_MIN_ATTACK` is folded into a
 * shared trigger/cooldown line instead of repeated per effect — see
 * `curseMechanicsExplanation` below.
 */
const CURSE_EXPLANATION: Readonly<Record<CurseEffect, string>> = {
  fog: `Fog — clouds your stack from view for ${seconds(CURSE_EFFECT_DURATION_MS.fog)}. Your active piece and the well's walls stay visible; you are placing blind underneath them.`,
  blind: `Blind — hides your next-piece queue for ${seconds(CURSE_EFFECT_DURATION_MS.blind)}. Hold stays visible, so it is still worth saving a piece before this one hits.`,
  mirror: `Mirror — swaps left and right for ${seconds(CURSE_EFFECT_DURATION_MS.mirror)}. Every other input is unaffected.`,
  rush: `Rush — multiplies your gravity ${String(CURSE_RUSH_GRAVITY_FACTOR)}x for ${seconds(CURSE_EFFECT_DURATION_MS.rush)}. Pieces fall twice as fast until it wears off.`,
};

export function explainCurseEffect(effect: CurseEffect): string {
  return CURSE_EXPLANATION[effect];
}

/** How a curse actually gets cast — the trigger and the cooldown, in one sentence, quoting the real numbers. */
export function explainCurseMechanics(): string {
  return (
    `Clearing a double or more (anything that would send at least ` +
    `${String(CURSE_TRIGGER_MIN_ATTACK)} point of attack) casts one random curse from your ` +
    `loadout at your opponent instead of sending garbage. A cooldown of ` +
    `${seconds(CURSE_COOLDOWN_MS)} per victim stops them stacking unreadably — you can always ` +
    `see what just hit you before the next one can land.`
  );
}

/** How the rope works, quoting its real scale. */
export function explainTug(): string {
  return (
    `One shared rope instead of knockouts. Every line you clear pulls it toward you; the ` +
    `bigger the clear, the harder the pull. First side to pull it all the way to their end ` +
    `(±${String(TUG_ROPE_SCALE)} charge units) wins the round — a top-out still loses it, the ` +
    `same as every other duel.`
  );
}

/** What the handicap marker (◆) next to a score means, quoting the real percentages. */
export function explainHandicap(): string {
  return (
    `The seat marked ◆ is behind and gets a small, automatic boost: eased gravity and eased ` +
    `incoming attack, never garbage they didn't earn. Two accounts with ` +
    `${String(HANDICAP_MIN_RECORDED_MATCHES)} or more finished matches between them use the gap ` +
    `in their win rates, up to ${String(HANDICAP_MAX_GRAVITY_EASE_PERMILLE / 10)}% eased ` +
    `gravity and ${String(Math.round(HANDICAP_MAX_ATTACK_EASE * 100))}% eased incoming attack; ` +
    `everyone else uses a flat ${String(HANDICAP_GUEST_GRAVITY_EASE_PERMILLE / 10)}%/` +
    `${String(Math.round(HANDICAP_GUEST_ATTACK_EASE * 100))}% ease for whoever is down a round in ` +
    `the match so far. It is on by default in a friendly room and off in a ranked one.`
  );
}

/** A longer explanation per room variant, beyond the one-line tagline `duelVariantTagline` already gives on the picker itself. */
const VARIANT_EXPLANATION: Readonly<Record<RoomVariant, string>> = {
  duel: `The classic. Clearing lines sends garbage at your opponent; a clear you make cancels garbage aimed at you. First to ${String(ROUNDS_TO_WIN)} rounds wins the match.`,
  duelSprint: `Both players race the identical piece sequence to the same goal — no garbage crosses at all, so a faster finish is the only thing that decides it.`,
  duelSurvival: `Everything duel has, plus a floor that rises under both wells on a shared timer. Outlast the rise and your opponent's mistakes both.`,
  curses: explainCurseMechanics(),
  tug: explainTug(),
  coopShared: `You and a partner share one well and one queue, with two active pieces moving at once — mind each other's piece as much as your own stack.`,
  coopWide: `A well twice the width, one player per half, one shared stack and a rising floor from the server. There is no opponent — just the clock and the two of you.`,
};

export function explainVariant(variant: RoomVariant): string {
  return VARIANT_EXPLANATION[variant];
}

/**
 * What each AI difficulty actually means, one line each.
 *
 * Deliberately *not* composed from constants, which is the opposite of every
 * other function in this file and worth saying why. The dials that decide how
 * the bot plays — reaction delay, actions per second, misdrop odds, judgement
 * noise, aggression — live in `apps/server/src/bot/difficulty.ts` and never
 * cross the wire, because no peer needs them: the bot's inputs arrive as
 * ordinary input events and every simulation of its board replays those.
 * Importing them here would put server-only numbers in the client bundle for
 * the sake of a sentence, and "places roughly 3.2 pieces per second" is not a
 * sentence that helps anybody choose a difficulty anyway.
 *
 * What keeps this copy honest instead is that every *comparative* claim it
 * makes — faster, tighter, blunders less — is a claim about the ordering of
 * those dials, and `apps/server/test/bot/difficulty.test.ts` asserts that
 * ordering directly. The copy can go stale about a number; it cannot go stale
 * about which level is harder.
 */
const AI_LEVEL_EXPLANATION: Readonly<Record<AiLevel, string>> = {
  easy: 'Easy — takes its time, mostly lets pieces settle rather than slamming them, and misplaces one fairly often. It clears whatever row is in front of it, so very little comes back at you.',
  normal:
    'Normal — thinks for about a third of a second, plays at a steady pace and blunders now and then. It will hold out for a bigger clear when one is available.',
  hard: 'Hard — reacts quickly, places fast and rarely misdrops. It keeps a column open for line pieces and cashes it in, so expect real pressure.',
  expert:
    'Expert — near-instant, places about as fast as the rules allow, and never fumbles. It plays for quads every time and takes them the moment they are there.',
};

/** One line on what a difficulty means, for the lobby's own picker. */
export function explainAiLevel(level: AiLevel): string {
  return AI_LEVEL_EXPLANATION[level];
}

/** Every difficulty explained at once, for a disclosure that lists them. */
export function explainAiLevels(): readonly string[] {
  return AI_LEVELS.map(explainAiLevel);
}

/** Why there is no handicap toggle on the vs-AI path. */
export function explainAiHandicap(): string {
  return 'There is no handicap against the computer: the difficulty is the handicap. Pick a level that gives you a close game and change it whenever you like — nothing is being adjusted behind your back.';
}

/**
 * The compact "how to play" basics — move, rotate, drop, hold — in one
 * breath, reachable from the start page per the owner's brief. Deliberately
 * device-neutral prose (the touch pads and the keyboard bindings are shown
 * elsewhere, on the screens that actually have them) rather than a table of
 * key codes that would drift from `input/keyboard.ts` the moment a binding
 * changed.
 */
export function howToPlayBasics(): readonly string[] {
  return [
    'Move a piece left or right, and soft-drop to speed its fall — it never locks early from that alone.',
    'Rotate it clockwise or counter-clockwise to fit a gap; a hard drop slams it straight down and locks it in place.',
    'Hold swaps the current piece for later, once per piece — the boxed slot on the rail shows what you are saving.',
    'Clear a full row to score it. On a phone the on-screen pads do all of this; a keyboard and a gamepad both work too.',
  ];
}
